import React, { useEffect, useState } from "react";
import { X, Download, Smartphone } from "lucide-react";
import {
    onInstallAvailability,
    triggerInstallPrompt,
    isStandalone,
} from "../serviceWorkerRegistration";

const DISMISS_KEY = "fitsme_pwa_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const _wasRecentlyDismissed = () => {
    try {
        const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
        return ts && Date.now() - ts < DISMISS_TTL_MS;
    } catch { return false; }
};

const _markDismissed = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_e) { /* private mode */ }
};

const _isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

/**
 * Bottom-pinned brutalist "Install Fits Me" toast.
 *
 *  - Chrome / Edge / Android: shows when `beforeinstallprompt` fires. Click =
 *    native install dialog.
 *  - iOS Safari: shows a manual "Tap Share → Add to Home Screen" walkthrough
 *    because Apple doesn't expose `beforeinstallprompt`.
 *  - Already-installed (PWA / standalone): renders nothing.
 *  - Dismissed in the last 7 days: renders nothing.
 */
export const InstallAppPrompt = () => {
    const [available, setAvailable] = useState(false);
    const [dismissed, setDismissed] = useState(_wasRecentlyDismissed());
    const [iosMode, setIosMode] = useState(false);

    useEffect(() => {
        if (isStandalone()) {
            setDismissed(true);
            return;
        }
        // Subscribe to native-install availability (Chrome/Edge/Android).
        const off = onInstallAvailability((isReady) => setAvailable(isReady));
        // iOS Safari fallback (only show after 15s + first scroll so it's not pushy).
        if (_isIos() && !isStandalone()) {
            const t = setTimeout(() => setIosMode(true), 15_000);
            return () => { off(); clearTimeout(t); };
        }
        return off;
    }, []);

    const close = () => {
        _markDismissed();
        setDismissed(true);
    };

    const install = async () => {
        const choice = await triggerInstallPrompt();
        if (choice.outcome === "accepted") {
            setDismissed(true);
        } else if (choice.outcome === "dismissed") {
            _markDismissed();
            setDismissed(true);
        }
    };

    if (dismissed) return null;
    if (!available && !iosMode) return null;

    return (
        <div
            data-testid="install-app-prompt"
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-40 border-4 border-black bg-[#CCFF00] shadow-brutal-lg"
        >
            <div className="flex items-start gap-3 p-4">
                <div className="border-2 border-black bg-black text-[#CCFF00] p-2 shadow-brutal-sm shrink-0">
                    <Smartphone className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-mono text-[10px] uppercase tracking-widest mb-1">/ Install /</p>
                    <h3 className="font-display text-base font-black uppercase tracking-tighter leading-none">
                        Fits Me on your home screen.
                    </h3>
                    {iosMode ? (
                        <p className="font-mono text-[11px] leading-relaxed mt-1.5">
                            Tap <span className="bg-black text-[#CCFF00] px-1.5 font-bold">Share</span> &rarr; <strong>&ldquo;Add to Home Screen&rdquo;</strong>.
                        </p>
                    ) : (
                        <p className="font-mono text-[11px] leading-relaxed mt-1.5">
                            One tap. Stand-alone window, offline-browsing, no app-store wait.
                        </p>
                    )}

                    {!iosMode && (
                        <button
                            onClick={install}
                            data-testid="install-app-prompt-install"
                            className="mt-2.5 inline-flex items-center gap-1.5 border-2 border-black bg-black text-[#CCFF00] px-3 py-1.5 font-display font-black text-xs uppercase tracking-widest shadow-brutal-sm btn-press"
                        >
                            <Download className="w-3 h-3" /> Install
                        </button>
                    )}
                </div>
                <button
                    onClick={close}
                    data-testid="install-app-prompt-close"
                    aria-label="Dismiss"
                    className="border-2 border-black bg-white p-1 shadow-brutal-sm btn-press shrink-0"
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
};
